(function() {
  'use strict';
  
  const PRODUCTION_MODE = false;
  let productCache = {};
  let productCacheTimestamps = {};
  const CACHE_TTL = 10 * 60 * 1000; // 10 dakika cache süresi
  let setGroups = new Map();
  let isUpdating = false;
  let debounceTimer = null;
  
  function log(...args) {
    if (!PRODUCTION_MODE) {
      console.log('[Cart Set]', ...args);
    }
  }

  // === Shared size/distribution helpers (paralel to quantity-multiplier.js) ===
  const EXCLUDED_WORDS = new Set(['TRY', 'USD', 'EUR', 'GBP', 'ADET', 'KG', 'TL', 'MAL', 'VE', 'BR', 'DIV', 'EM', 'TEK', 'STD']);
  const VALID_SIZES = new Set(['S', 'M', 'L', 'XL', 'XXL', 'XXXL', 'XS']);
  const EXTENDED_SIZES = new Set(['1XL', '2XL', '3XL', '4XL', '5XL', '6XL', '7XL']);

  function isValidSize(size) {
    if (!size) return false;
    // Parantez temizliği: "(140-146)" → "140-146", "(LXL-SM)" → "LXL-SM"
    size = size.toUpperCase().replace(/[()]/g, '').trim();
    if (!size) return false;
    if (EXCLUDED_WORDS.has(size)) return false;
    if (VALID_SIZES.has(size) || EXTENDED_SIZES.has(size)) return true;
    if (/^\d{2,3}$/.test(size)) {
      const n = parseInt(size);
      return n >= 18 && n <= 200;
    }
    if (size.includes('-')) {
      const parts = size.split('-');
      return parts.length === 2 && parts[0] && parts[1] && isValidSize(parts[0]) && isValidSize(parts[1]);
    }
    if (size.includes('/')) {
      const parts = size.split('/');
      return parts.length === 2 && parts[0] && parts[1] && isValidSize(parts[0]) && isValidSize(parts[1]);
    }
    return false;
  }

  function parseToken(token, dist) {
    if (!token) return false;
    let m = token.match(/^\(([\w/-]+)\)\s*[-:=]\s*(\d{1,3})$/);
    if (m) {
      const size = m[1].toUpperCase();
      const qty = parseInt(m[2]);
      if (qty > 0 && qty <= 100 && isValidSize(size)) { dist[size] = qty; return true; }
    }
    m = token.match(/^(.+?)\s*[-:=]\s*(\d{1,3})$/);
    if (m) {
      const size = m[1].replace(/[()]/g, '').toUpperCase().trim();
      const qty = parseInt(m[2]);
      if (qty > 0 && qty <= 100 && isValidSize(size)) { dist[size] = qty; return true; }
    }
    m = token.match(/^(\d+)([A-Z]+)$/i);
    if (m) {
      const qty = parseInt(m[1]);
      const sizeBase = m[2].toUpperCase();
      if (EXTENDED_SIZES.has(qty + sizeBase) && isValidSize(qty + sizeBase)) { dist[qty + sizeBase] = 1; return true; }
      if (qty > 0 && qty <= 100 && VALID_SIZES.has(sizeBase)) { dist[sizeBase] = qty; return true; }
    }
    m = token.match(/^([A-Z]+)(\d{1,2})$/i);
    if (m) {
      const size = m[1].toUpperCase();
      const qty = parseInt(m[2]);
      if (qty > 0 && qty <= 100 && VALID_SIZES.has(size)) { dist[size] = qty; return true; }
    }
    m = token.match(/^(\d{2,3})$/);
    if (m && isValidSize(m[1])) { dist[m[1]] = 1; return true; }
    m = token.match(/^([A-Z]+)$/i);
    if (m) {
      const size = m[1].toUpperCase();
      if (VALID_SIZES.has(size) || EXTENDED_SIZES.has(size)) { dist[size] = 1; return true; }
    }
    return false;
  }

  // Bir cart line item'ın renk-anahtarını döndürür ("|GREEN" formatında — Liquid'in oluşturduğu data-set-color ile uyumlu).
  // variant_options array'inden beden olmayan değerleri alır. Beden tespiti isValidSize() ile.
  function getItemColorKey(item) {
    if (!item) return '';
    const opts = item.variant_options || item.options || [];
    if (!Array.isArray(opts) || opts.length === 0) {
      // Fallback: variant_title'ı slash'la böl, beden olmayanları al
      if (item.variant_title) {
        const parts = item.variant_title.split('/').map(p => p.trim()).filter(Boolean);
        const colors = parts.filter(p => !isValidSize(p.toUpperCase().replace(/[()]/g, '')));
        return colors.length ? '|' + colors.join('|') : '';
      }
      return '';
    }
    const colors = opts.filter(v => {
      if (!v) return false;
      const upper = String(v).toUpperCase().trim();
      return !isValidSize(upper);
    });
    return colors.length ? '|' + colors.join('|') : '';
  }

  function tokenizeAndParseDistribution(text) {
    const dist = {};

    // ÖNCE: "NxSIZE" formatı (örn. "1x38-1x40-1x42") — boşluk içermeyen multi-size token için global tarama.
    // KRİTİK: "x" sadece lowercase match etsin — "1XL"deki "X" çarpan ayracı sanılmasın.
    const xMatches = [...text.matchAll(/(\d+)\s*x\s*(\d{2,3}|[A-Za-z]{1,4})(?=[-\s,]|$)/g)];
    if (xMatches.length > 0) {
      for (const m of xMatches) {
        const qty = parseInt(m[1]);
        const size = m[2].toUpperCase();
        if (qty > 0 && qty <= 100 && isValidSize(size)) {
          dist[size] = qty;
        }
      }
      if (Object.keys(dist).length > 0) return dist;
    }

    const tokens = text.split(/[\s,]+/).map(t => t.trim()).filter(t => t && !/^[-:=]+$/.test(t));
    for (const token of tokens) parseToken(token, dist);
    return dist;
  }
  
  // Kapsamlı set bilgisi parse fonksiyonu (quantity-multiplier.js'den uyarlanmış)
  function parseCompleteSetInfo(description, productTitle) {
    if (!description) return null;

    // DEBUG: Ham description'ı logla
    log('RAW DESCRIPTION:', description.substring(0, 500));

    let cleanText = description.toString().trim();
    cleanText = cleanText.replace(/<[^>]*>/g, '');
    cleanText = cleanText.replace(/&[a-zA-Z0-9#]+;/g, '');
    cleanText = cleanText.replace(/^\*+/g, '');
    cleanText = cleanText.replace(/\*+$/g, '');
    cleanText = cleanText.replace(/\*/g, '');
    cleanText = cleanText.replace(/[\r\n\t\f\v]/g, ' ');
    cleanText = cleanText.replace(/\s+/g, ' ');
    cleanText = cleanText.trim();

    log('CLEAN TEXT:', cleanText.substring(0, 500));
    
    // 1. BEDEN SETİ FORMATI
    if (cleanText.includes('Beden Seti:') && cleanText.includes('Dağılım:')) {
      const variantDistributions = {};
      const sections = cleanText.split('Beden Seti:');
      
      for (let i = 1; i < sections.length; i++) {
        const section = sections[i];
        const dagitimParts = section.split('Dağılım:');
        
        if (dagitimParts.length > 1) {
          const bedenRange = dagitimParts[0].replace(/[()]/g, '').trim();
          const dagitimContent = dagitimParts[1].split(/Barkod|Teknik|$/)[0].trim();
          
          let totalQty = 0;
          const sizeMap = {};
          
          // XS:1 - S:1 - M:1 - L:1 ve 26:1 - 27:1 ve S/M:2 formatlarını parse et
          const matches = dagitimContent.matchAll(/([A-Z]{1,3}(?:\/[A-Z]{1,3})?|\d{1,3}):(\d+)/g);
          for (const match of matches) {
            const size = match[1];
            const qty = parseInt(match[2]);
            sizeMap[size] = qty;
            totalQty += qty;
          }
          
          if (totalQty > 0) {
            variantDistributions[bedenRange] = {
              total: totalQty,
              sizes: sizeMap
            };
          }
        }
      }
      
      if (Object.keys(variantDistributions).length > 0) {
        // Her range için toplam miktarı hesapla
        const totalPerRange = {};
        for (const [range, info] of Object.entries(variantDistributions)) {
          totalPerRange[range] = info.total;
        }

        log('Beden Seti formatı:', variantDistributions);
        return {
          type: 'beden_seti',
          distributions: variantDistributions,
          isMultiSize: true,
          totalPerRange: totalPerRange
        };
      }
    }
    
    // 2. SET İÇERİK BİLGİSİ FORMATI (S-2 M-2 L-2 veya 2S-2M-2L)
    let setContent = null;

    // Hem Türkçe İçerik hem ASCII Icerik varyantını destekle
    const setLabelVariant = cleanText.includes('Set İçerik Bilgisi:') ? 'Set İçerik Bilgisi:' :
                            cleanText.includes('Set Icerik Bilgisi:') ? 'Set Icerik Bilgisi:' : null;

    if (setLabelVariant) {
      const parts = cleanText.split(setLabelVariant);
      if (parts.length > 1) {
        const endMarkers = ['Teknik', 'Kumaş', 'Kalıp', 'Kol', 'Beden', 'Bakım', 'Ürün', 'Barkod'];
        let content = parts[1];
        for (const marker of endMarkers) {
          if (content.includes(marker)) {
            content = content.split(marker)[0];
            break;
          }
        }
        setContent = content.trim();
      }
    }
    
    // 3. DAĞILIM FORMATI
    if (!setContent && cleanText.includes('Dağılım:')) {
      const parts = cleanText.split('Dağılım:');
      if (parts.length > 1) {
        setContent = parts[1].split(/Teknik|Kumaş|Barkod/)[0].trim();
      }
    }

    // DEBUG: Set içeriğini logla
    if (setContent) {
      log('SET CONTENT (RAW):', JSON.stringify(setContent));
      log('SET CONTENT (length):', setContent.length);
    }

    // Set içeriğini parse et
    if (setContent) {
      // YENİ: Önce shared token-based parser dene — compound range "S-M-2 L-XL-2 2XL-3XL-2",
      //       slash beden "M/L:1", ":" ayracı "S:1 - M:1" hepsini doğru handle eder
      const tokenDist = tokenizeAndParseDistribution(setContent);
      if (Object.keys(tokenDist).length > 0) {
        const totalPerSet = Object.values(tokenDist).reduce((s, q) => s + q, 0);
        log('Token-based parse (cart):', tokenDist);
        return { type: 'multi_size_set', distribution: tokenDist, isMultiSize: true, totalPerSet };
      }

      const distribution = {};
      let hasMultipleSizes = false;

      // Geçerli beden isimleri - quantity-multiplier.js ile uyumlu (legacy fallback path)
      const excludedWords = EXCLUDED_WORDS;
      const validSizes = new Set([...VALID_SIZES, ...EXTENDED_SIZES]);
      const extendedSizes = EXTENDED_SIZES;

      // YENİ: Yaş bazlı beden formatı - "7-8-1  9-10-1  11-12-1"
      const ageRangeMatches = [...setContent.matchAll(/(\d{1,2}-\d{1,2})-(\d{1,2})/g)];
      if (ageRangeMatches.length > 0) {
        log('Age-range format detected in cart parser');
        for (const match of ageRangeMatches) {
          const ageRange = match[1];
          const quantity = parseInt(match[2]);
          if (quantity > 0 && quantity <= 100) {
            distribution[ageRange] = quantity;
            hasMultipleSizes = true;
          }
        }
        if (hasMultipleSizes) {
          const totalPerSet = Object.values(distribution).reduce((sum, qty) => sum + qty, 0);
          return { type: 'multi_size_set', distribution, isMultiSize: true, totalPerSet };
        }
      }

      // YENİ: Extended size+qty formatı - "1XL1-2XL1-3XL1-4XL1"
      const extSizeQtyMatches = [...setContent.matchAll(/(\d[A-Z]{1,3})(\d{1,2})(?=[-\s]|$)/gi)];
      if (extSizeQtyMatches.length > 0) {
        for (const match of extSizeQtyMatches) {
          const sizeName = match[1].toUpperCase();
          const quantity = parseInt(match[2]);
          if (extendedSizes.has(sizeName) && quantity > 0) {
            distribution[sizeName] = quantity;
            hasMultipleSizes = true;
          }
        }
        if (hasMultipleSizes) {
          const totalPerSet = Object.values(distribution).reduce((sum, qty) => sum + qty, 0);
          return { type: 'multi_size_set', distribution, isMultiSize: true, totalPerSet };
        }
      }

      // YENİ: Extended size isimleri adet olmadan - "1XL-2XL-3XL-4XL"
      const extSizeOnlyMatches = [...setContent.matchAll(/(\d[A-Z]{1,3})(?=[-\s]|$)/gi)];
      if (extSizeOnlyMatches.length > 0) {
        let extFound = false;
        for (const match of extSizeOnlyMatches) {
          const sizeName = match[1].toUpperCase();
          if (extendedSizes.has(sizeName)) {
            distribution[sizeName] = 1;
            extFound = true;
            hasMultipleSizes = true;
          }
        }
        if (extFound && Object.keys(distribution).length > 0) {
          const totalPerSet = Object.values(distribution).reduce((sum, qty) => sum + qty, 0);
          return { type: 'multi_size_set', distribution, isMultiSize: true, totalPerSet };
        }
      }

      // YENİ: SIZE+QTY bitişik format - "S1-M1-L1-XL1"
      const sizeQtyAttachedMatches = [...setContent.matchAll(/([A-Z]{1,4})(\d{1,2})(?=[-\s]|$)/gi)];
      if (sizeQtyAttachedMatches.length > 0) {
        for (const match of sizeQtyAttachedMatches) {
          const size = match[1].toUpperCase();
          const quantity = parseInt(match[2]);
          if (validSizes.has(size) && !excludedWords.has(size) && !extendedSizes.has(size) && quantity > 0) {
            distribution[size] = quantity;
            hasMultipleSizes = true;
          }
        }
        if (hasMultipleSizes) {
          const totalPerSet = Object.values(distribution).reduce((sum, qty) => sum + qty, 0);
          return { type: 'multi_size_set', distribution, isMultiSize: true, totalPerSet };
        }
      }

      // YENİ: Parantez formatı - "(S-M)-3" veya "(S-M):3"
      const parenthesisPattern = /\(([A-Z]+(?:-[A-Z]+)*)\)\s*[-:=]\s*(\d+)/gi;
      const parenthesisMatches = [...setContent.matchAll(parenthesisPattern)];

      if (parenthesisMatches.length > 0) {
        log('Parenthesis format detected in cart parser');
        for (const match of parenthesisMatches) {
          const sizeName = match[1].toUpperCase();
          const quantity = parseInt(match[2]);
          if (quantity > 0 && quantity <= 100) {
            distribution[sizeName] = quantity;
            hasMultipleSizes = true;
            log(`Parenthesis format found: (${sizeName})-${quantity}`);
          }
        }
      }

      // YENI: Tire içeren beden isimleri - "S-M-3" veya "L-XL:2"
      // Önce potansiyel tire içeren beden isimlerini tespit et
      if (!hasMultipleSizes) {
        // "S-M-3" veya "S-M:3" veya "S-M=3" formatları
        const compoundSizePattern = /([A-Z]+-[A-Z]+)\s*[-:=]\s*(\d+)/gi;
        const compoundMatches = [...setContent.matchAll(compoundSizePattern)];

        if (compoundMatches.length > 0) {
          log('Compound size format detected in cart parser');
          for (const match of compoundMatches) {
            const sizeName = match[1].toUpperCase();
            const quantity = parseInt(match[2]);
            if (quantity > 0 && quantity <= 100) {
              distribution[sizeName] = quantity;
              hasMultipleSizes = true;
              log(`Compound size found: ${sizeName}-${quantity}`);
            }
          }
        }
      }

      // Önce "S-2 M-2 L-4 XL-4" veya "44-1 46-2" formatını dene (size-quantity)
      if (!hasMultipleSizes) {
        const sizeQuantityPattern = /([A-Z]{1,4}|\d{2,3})\s*[-:]\s*(\d{1,2})/g;
        let matches = [...setContent.matchAll(sizeQuantityPattern)];

        if (matches.length > 0) {
          log('Size-quantity pattern matched, count:', matches.length);
          matches.forEach(match => {
            const size = match[1].toUpperCase();
            const qty = parseInt(match[2]);
            const isNumericSize = /^\d{2,3}$/.test(size);
            // Harf bedenler: validSizes kontrolü, Numeric bedenler: direkt kabul
            if ((isNumericSize || (validSizes.has(size) && !excludedWords.has(size))) && qty > 0 && qty <= 100) {
              distribution[size] = qty;
              hasMultipleSizes = true;
              log(`Size-quantity found: ${size}-${qty}`);
            }
          });
        }
      }

      // Eğer size-quantity pattern çalışmadıysa, "2S-2M-4L-4XL" formatını dene
      if (!hasMultipleSizes) {
        const quantitySizePattern = /(\d+)\s*([A-Z]{1,4})(?=\s|-|$)/g;
        let matches = [...setContent.matchAll(quantitySizePattern)];

        if (matches.length > 0) {
          log('Quantity-size pattern matched, count:', matches.length);
          matches.forEach(match => {
            const qty = parseInt(match[1]);
            const size = match[2].toUpperCase();
            // SADECE geçerli beden isimleri kabul et
            if (validSizes.has(size) && !excludedWords.has(size) && qty > 0 && qty <= 100) {
              distribution[size] = qty;
              hasMultipleSizes = true;
              log(`Quantity-size found: ${qty}${size}`);
            }
          });
        }
      }
      
      if (hasMultipleSizes && Object.keys(distribution).length > 0) {
        // Toplam miktar hesapla
        const totalPerSet = Object.values(distribution).reduce((sum, qty) => sum + qty, 0);

        log('Multi-size set bulundu:', distribution);
        return {
          type: 'multi_size_set',
          distribution: distribution,
          isMultiSize: true,
          totalPerSet: totalPerSet
        };
      }
    }
    
    // 4. PAKET YAPISI FORMATI — artık multi-variant multi-size set olarak işlenir
    //    "1S+1M+1L+1XL" → her bedenden ilgili adet; "36+38+40+42" → her sayısal bedenden 1 adet
    if (cleanText.includes('Paket Yapısı:')) {
      // Sadece sayı/A-Z/+/whitespace yiyen sıkı regex — Türkçe küçük harf veya end-marker'a rastlayınca dur.
      // Eski: ".+?(Teknik|Kumaş|Barkod|$)" — description boşluksuz devam ederse tüm metni yutuyordu ("2SM+2LXLKadın..." → split bozulur).
      const paketMatch = cleanText.match(/Paket Yapısı:\s*([\d+A-Z\s-]+?)(?=\s*[A-ZÇĞİÖŞÜ][a-zçğıöşüİı]|Teknik|Kumaş|Barkod|$)/);
      if (paketMatch) {
        const paketContent = paketMatch[1].trim();
        const plusParts = paketContent.split('+').map(p => p.trim()).filter(Boolean);
        const distribution = {};

        for (const part of plusParts) {
          // "1S", "2M", "1XL" — qty + harf beden
          let m = part.match(/^(\d+)([A-Z]+)$/i);
          if (m) {
            const qty = parseInt(m[1]);
            const size = m[2].toUpperCase();
            if (qty > 0 && qty <= 100) {
              distribution[size] = (distribution[size] || 0) + qty;
            }
            continue;
          }
          // "1SM", "2LXL" — qty + compound harf beden (ör. SM = S+M paketi)
          m = part.match(/^(\d+)([A-Z]{2,})$/i);
          if (m) {
            const qty = parseInt(m[1]);
            const size = m[2].toUpperCase();
            if (qty > 0 && qty <= 100) {
              distribution[size] = (distribution[size] || 0) + qty;
            }
            continue;
          }
          // Sadece sayısal: "36", "38" — her birinden 1 adet
          m = part.match(/^(\d{2,3})$/);
          if (m) {
            distribution[m[1]] = (distribution[m[1]] || 0) + 1;
            continue;
          }
          // Sadece harf: "S", "M" — her birinden 1 adet
          m = part.match(/^([A-Z]+)$/i);
          if (m) {
            const size = m[1].toUpperCase();
            distribution[size] = (distribution[size] || 0) + 1;
          }
        }

        if (Object.keys(distribution).length > 0) {
          const totalPerSet = Object.values(distribution).reduce((s, q) => s + q, 0);
          log('Paket Yapısı → multi-size:', distribution, 'totalPerSet:', totalPerSet);
          return {
            type: 'multi_size_set',
            distribution: distribution,
            isMultiSize: true,
            totalPerSet: totalPerSet
          };
        }
      }
    }

    // 5. STD FORMATI
    // "5xSTANDART", "5xSTD", "STD-5", "STD:5" gibi formatları destekle
    let stdMatch = cleanText.match(/(\d+)\s*x\s*STAN[DA]*R[DT]/i);
    if (!stdMatch) {
      stdMatch = cleanText.match(/\bSTD\s*[-:]?\s*(\d+)/i);
    }
    if (!stdMatch) {
      stdMatch = cleanText.match(/(\d+)\s*x\s*STD/i);
    }

    if (stdMatch) {
      const multiplier = parseInt(stdMatch[1]);
      log('STD formatı bulundu:', multiplier, 'Pattern:', stdMatch[0]);
      return {
        type: 'std',
        multiplier: multiplier,
        isMultiSize: false
      };
    }

    // 6. ÜRÜN ADINDAN SET BİLGİSİ
    if (productTitle) {
      const titleUpper = productTitle.toUpperCase();
      const setPatterns = [
        /(\d+)\s*[''']?\s*[Ll][İIıi]\s*[Ss][Ee][Tt]/,
        /(\d+)\s*[Aa][Dd][Ee][Tt]/,
        /(\d+)\s*[Ll][İIıi]\s*[Pp][Aa][Kk][Ee][Tt]/
      ];
      
      for (const pattern of setPatterns) {
        const match = titleUpper.match(pattern);
        if (match) {
          const multiplier = parseInt(match[1]);
          if (multiplier > 1) {
            return {
              type: 'title_based',
              multiplier: multiplier,
              isMultiSize: false
            };
          }
        }
      }
    }
    
    return null;
  }
  
  // Ürün bilgilerini al (cache TTL destekli)
  async function getProductSetInfo(handle) {
    // Cache'de varsa ve süresi dolmamışsa kullan
    if (productCache[handle] && productCacheTimestamps[handle]) {
      if (Date.now() - productCacheTimestamps[handle] < CACHE_TTL) {
        return productCache[handle];
      }
      // Süresi dolmuş, temizle
      delete productCache[handle];
      delete productCacheTimestamps[handle];
    }

    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) return null;

      const product = await response.json();
      const setInfo = parseCompleteSetInfo(product.description, product.title);

      if (setInfo) {
        setInfo.handle = handle;
        setInfo.productId = product.id;
        setInfo.title = product.title;
        setInfo.variants = product.variants;

        productCache[handle] = setInfo;
        productCacheTimestamps[handle] = Date.now();
        log(`Set bilgisi (${handle}):`, setInfo);
      }

      return setInfo;
    } catch (error) {
      log('Ürün bilgisi alınamadı:', error);
      return null;
    }
  }
  
  // Sepetteki set ürünlerini grupla
  async function groupSetItems() {
    let cart;
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return;
      cart = await response.json();
    } catch (error) {
      log('Cart verisi alınamadı:', error);
      return;
    }
    if (!cart || !cart.items) return;
    setGroups.clear();
    
    for (const item of cart.items) {
      const handle = item.handle || item.url?.split('/products/')[1]?.split('?')[0];
      if (!handle) continue;
      
      const setInfo = await getProductSetInfo(handle);
      if (!setInfo) continue;
      
      // Multi-size set için gruplama
      if (setInfo.isMultiSize) {
        if (!setGroups.has(handle)) {
          // Distribution'ı normalize et - hem 'distribution' hem 'distributions' olabilir
          let normalizedDistribution = {};
          let isBedenSeti = false;

          if (setInfo.distribution) {
            normalizedDistribution = setInfo.distribution;
          } else if (setInfo.distributions) {
            isBedenSeti = true;
            // Beden seti formatı — range isimlerini key olarak kullan (XS-L, XL-XXL gibi)
            // Range'in total'ini value olarak koy
            for (const [range, info] of Object.entries(setInfo.distributions)) {
              normalizedDistribution[range] = info.total || Object.values(info.sizes || {}).reduce((s, q) => s + q, 0);
            }
            log('Beden Seti: range-based distribution:', normalizedDistribution);
          }

          setGroups.set(handle, {
            info: setInfo,
            items: [],
            distribution: normalizedDistribution,
            isBedenSeti: isBedenSeti,
            totalPerSet: setInfo.totalPerSet || setInfo.totalPerRange || Object.values(normalizedDistribution).reduce((sum, qty) => sum + qty, 0)
          });
        }

        const group = setGroups.get(handle);

        // Hangi bedene ait olduğunu bul
        const variantTitle = item.variant_title || item.title || '';
        log(`--- Cart item: "${item.product_title}" ---`);
        log(`Variant title: "${variantTitle}"`);
        log(`Quantity: ${item.quantity}`);
        log(`Key: ${item.key}`);

        // Variant title'dan beden çıkar - slash varsa her iki parçayı da kontrol et
        // Çünkü format "RENK / BEDEN" veya "BEDEN / RENK" veya "(XS-L) / RENK" olabilir
        const parts = variantTitle.split('/').map(p => p.trim().toUpperCase().replace(/[()]/g, '').trim());
        log(`Variant parts:`, parts);

        let sizeKey = null;
        let isCompoundVariant = false;

        // Önce normalized distribution'dan kontrol et - UZUNDAN KISAYA sırala (tire içeren bedenler / range isimleri önce)
        if (group.distribution && Object.keys(group.distribution).length > 0) {
          const sizes = Object.keys(group.distribution).sort((a, b) => b.length - a.length);
          log(`Checking against distribution (sorted):`, sizes);

          // Her variant part'ı kontrol et
          for (const variantPart of parts) {
            if (sizeKey) break; // Bulunduysa dur

            for (const size of sizes) {
              const sizeUpper = size.toUpperCase();

              // TAM EŞLEŞMEye öncelik ver (parantez temizlenmiş variant ile)
              if (variantPart === sizeUpper) {
                sizeKey = size;
                log(`✓ Size matched from distribution (exact): ${size} from part "${variantPart}"`);
                break;
              }

              // Tire içeren bedenler için kısmi eşleşme: "S-M" içinde "S" var mı?
              // (Beden Seti değilse — individual size eşleştirmesi)
              if (!group.isBedenSeti && sizeUpper.includes('-')) {
                const sizeParts = sizeUpper.split('-').map(s => s.trim());
                if (sizeParts.includes(variantPart)) {
                  sizeKey = size;
                  log(`✓ Size matched from distribution (partial in compound): ${size} contains ${variantPart}`);
                  break;
                }
              }
            }
          }
        }

        // YENİ: Compound variant matching — variant title kendisi compound (örn. "(LXL-SM)") ve dağılımdaki
        // bedenlerden birini içeriyorsa, bu line item TÜM paketi temsil ediyor demektir (Paket Yapısı qty>1 case'i).
        // expectedQty = 1 (her line item bir paket = setCount = line_item.quantity)
        if (!sizeKey && group.distribution && Object.keys(group.distribution).length > 0) {
          const distKeys = Object.keys(group.distribution).map(k => k.toUpperCase());
          for (const variantPart of parts) {
            if (variantPart.includes('-')) {
              const variantSegments = variantPart.split('-').map(s => s.trim());
              if (distKeys.some(dk => variantSegments.includes(dk))) {
                sizeKey = '__compound__';
                isCompoundVariant = true;
                log(`✓ Compound variant detected: "${variantPart}" represents whole pack`);
                break;
              }
            }
          }
        }

        // Size option'lardan bul - UZUNDAN KISAYA
        if (!sizeKey) {
          const sizeOptions = ['XXXL', 'XXL', 'XL', 'L', 'M', 'S', 'XS'];
          log(`Checking against size options (sorted):`, sizeOptions);

          for (const variantPart of parts) {
            if (sizeKey) break;
            for (const size of sizeOptions) {
              if (variantPart === size || variantPart.startsWith(size + ' ') || variantPart === size) {
                sizeKey = size;
                log(`✓ Size matched from options: ${size} from part "${variantPart}"`);
                break;
              }
            }
          }
        }

        // SON FALLBACK: hiçbir eşleşme yok ve distribution multi-key ise → compound paket varsay
        // (ouz11088 gibi size option'u olmayan ürünler — variant adı renk, plugin ürün sayfasında STD'ye collapse etmiş,
        //  cart'a tek line item ekledi, line_item.quantity = totalPerSet)
        if (!sizeKey && group.distribution && Object.keys(group.distribution).length > 1) {
          sizeKey = '__compound__';
          isCompoundVariant = true;
          log(`✓ Compound paket fallback: no size match, multi-key dist → treating as whole pack`);
        }

        if (sizeKey) {
          // Compound variant: expectedQty = totalPerSet (paket başına toplam ürün).
          // line_item.quantity = setCount × totalPerSet → setCount = quantity / totalPerSet.
          let expectedQty;
          if (isCompoundVariant) {
            const totalPerSet = Object.values(group.distribution).reduce((s, q) => s + q, 0);
            expectedQty = totalPerSet > 0 ? totalPerSet : 1;
          } else {
            expectedQty = group.distribution?.[sizeKey] || 1;
          }
          log(`✓ Adding to group: size=${sizeKey}, expected=${expectedQty}, current=${item.quantity}, compound=${isCompoundVariant}`);
          group.items.push({
            ...item,
            size: sizeKey,
            expectedQty: expectedQty,
            isCompound: isCompoundVariant,
            key: item.key
          });
        } else {
          log(`✗ Size NOT found for variant: "${variantTitle}"`);
        }
      } else {
        // Tek varyantlı set ürünler
        if (!setGroups.has(handle)) {
          setGroups.set(handle, {
            info: setInfo,
            items: [item],
            multiplier: setInfo.multiplier || 1
          });
        }
      }
    }
    
    log('Set grupları oluşturuldu:', setGroups);
    return setGroups;
  }

  // Komple set silme fonksiyonu - YENİ!
  async function removeCompleteSet(itemKey) {
    try {
      // Önce cart'ı al ve set gruplarını güncelle
      const cart = await fetch('/cart.js').then(r => r.json());
      await groupSetItems();
      
      // Bu item'ın hangi set'e ait olduğunu bul
      const cartItem = cart.items.find(item => item.key === itemKey);
      if (!cartItem) return false;
      
      const handle = cartItem.handle || cartItem.url?.split('/products/')[1]?.split('?')[0];
      if (!handle) return false;
      
      const group = setGroups.get(handle);
      if (!group) {
        // Set ürünü değilse normal silme işlemi yap
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: itemKey, quantity: 0 })
        });
        return true;
      }
      
      // Set ürünüyse tüm set'i sil
      isUpdating = true;

      const updates = {};
      // Renk filtresi: tıklanan kartın rengine ait line item'ları sil; farklı renkleri etkileme
      const itemColor = getItemColorKey(cartItem);

      if (group.info.isMultiSize) {
        // Sadece aynı renkli line item'ları sil
        const targetItems = group.items.filter(li => getItemColorKey(li) === itemColor);
        for (const item of targetItems) {
          updates[item.key] = 0;
        }
        log(`Multi-size set siliniyor: ${handle} (renk: ${itemColor})`, Object.keys(updates));
      } else {
        // Tek varyantlı set: sadece o ürünü sil
        updates[itemKey] = 0;
        log(`Single-variant set siliniyor: ${handle}`, itemKey);
      }
      
      // Toplu güncelleme yap
      await batchUpdateCart(updates);
      
      // Başarı bildirimi
      const setType = group.info.isMultiSize ? 'Multi-size' : 'Single';
      showNotification(`${setType} set tamamen silindi: ${group.info.title}`, 'success');
      
      return true;
      
    } catch (error) {
      log('Set silme hatası:', error);
      showNotification('Set silme işleminde hata oluştu', 'warning');
      return false;
    } finally {
      isUpdating = false;
    }
  }

  // Kaldır linklerini intercept et - YENİ!
  function interceptRemoveLinks() {
    // Mevcut kaldır linklerini yakala
    const removeLinks = document.querySelectorAll('a[href*="/cart/change"][href*="quantity=0"], a[aria-label*="kaldır"], a[aria-label*="Kaldır"], .cart-remove, .remove-item');
    
    removeLinks.forEach(link => {
      if (link.dataset.intercepted) return; // Zaten intercept edilmiş
      link.dataset.intercepted = 'true';
      
      link.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // URL'den item key'ini al
        let itemKey = null;
        const href = link.href || link.getAttribute('href');
        
        if (href) {
          const match = href.match(/[?&]id=([^&]+)/);
          if (match) {
            itemKey = match[1];
          }
        }
        
        // Alternatif olarak, parent element'lerden item key'ini bul
        if (!itemKey) {
          const cartItem = link.closest('[data-cart-item], .cart-item, .cart__item, tr[data-product-id]');
          if (cartItem) {
            const quantityInput = cartItem.querySelector('input[name*="updates"]');
            if (quantityInput) {
              const name = quantityInput.name;
              const match = name.match(/updates\[([^\]]+)\]/);
              if (match) {
                itemKey = match[1];
              }
            }
          }
        }
        
        if (itemKey) {
          log('Kaldır butonuna tıklandı, item key:', itemKey);
          const success = await removeCompleteSet(itemKey);
          
          if (success) {
            await refreshCart();
          }
        } else {
          log('Item key bulunamadı, normal silme işlemi yapılıyor');
          // Fallback: normal Shopify silme işlemi
          window.location.href = href;
        }
      });
    });
  }
  
  // Multi-size set için tüm bedenleri güncelle.
  // colorKey: aynı ürünün farklı renkleri ayrı kartlar; sadece bu rengin line item'ları güncellenir.
  async function updateMultiSizeSet(handle, operation, colorKey) {
    // Race condition koruması: güncelleme devam ediyorsa bekle
    if (isUpdating) {
      log('Update already in progress, skipping');
      return;
    }

    const group = setGroups.get(handle);
    if (!group || !group.info.isMultiSize) return;

    // Renk filtresi: sadece bu rengin line item'ları
    const targetItems = colorKey
      ? group.items.filter(li => getItemColorKey(li) === colorKey)
      : group.items;

    if (targetItems.length === 0) {
      log('No items match color filter, abort');
      return;
    }

    log('=== UPDATE MULTI-SIZE SET ===');
    log('Handle:', handle, 'Color:', colorKey);
    log('Operation:', operation);
    log('Target items count:', targetItems.length, '/', group.items.length);
    log('Target items:', targetItems.map(i => `${i.size}:${i.quantity} (expected:${i.expectedQty})`));

    const updates = {};

    let totalExpectedQty = 0;
    let totalActualQty = 0;
    for (const item of targetItems) {
      totalExpectedQty += item.expectedQty;
      totalActualQty += item.quantity;
    }
    const currentSets = totalExpectedQty > 0
      ? Math.max(0, Math.round(totalActualQty / totalExpectedQty))
      : 0;
    log('Current sets:', currentSets);

    let newSets;
    if (operation === 'increase') {
      newSets = currentSets + 1;
    } else if (operation === 'decrease') {
      newSets = Math.max(0, currentSets - 1);
    } else {
      newSets = Math.max(0, parseInt(operation) || 0);
    }
    log('New sets:', newSets);

    for (const item of targetItems) {
      const newQty = newSets * item.expectedQty;
      updates[item.key] = newQty;
      log(`UPDATE: ${item.size}: ${item.quantity} → ${newQty}`);
    }

    // Eksik bedenleri kontrol et ve ekle (compound ürünlerde skip ediliyor)
    if (newSets > 0 && group.info.distribution) {
      await addMissingVariants(handle, group, newSets);
    }

    await batchUpdateCart(updates);

    const changeText = operation === 'increase' ? '+1 set'
      : operation === 'decrease' ? '-1 set'
      : `${newSets} set`;
    const itemsText = targetItems.map(item => `${item.size}: ${item.expectedQty * newSets}`).join(', ');
    showNotification(`${group.info.title}: ${changeText} (${itemsText})`, 'success');
  }
  
  // Eksik varyantları sepete ekle
  async function addMissingVariants(handle, group, sets) {
    if (sets === 0) return;

    // Compound variant grubu: tek line item zaten tüm paketi temsil ediyor, "eksik beden" diye bir şey yok
    if (group.items.length > 0 && group.items[0].isCompound) {
      log('Compound variant group — addMissingVariants skipped');
      return;
    }

    const existingSizes = group.items.map(item => item.size);
    const missingSizes = Object.keys(group.distribution).filter(size => !existingSizes.includes(size));

    if (missingSizes.length === 0) return;
    
    log('Eksik bedenler bulundu:', missingSizes);
    
    try {
      const product = await fetch(`/products/${handle}.js`).then(r => r.json());
      
      for (const size of missingSizes) {
        // İlgili varyantı bul
        const variant = product.variants.find(v => {
          const title = (v.title || '').toUpperCase();
          return title.includes(size) || v.option1 === size || v.option2 === size || v.option3 === size;
        });
        
        if (variant && variant.available) {
          const qty = group.distribution[size] * sets;
          
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: variant.id,
              quantity: qty
            })
          });
          
          log(`Eksik beden eklendi: ${size} - ${qty} adet`);
        }
      }
      
      // Grupları yenile
      await groupSetItems();
    } catch (error) {
      log('Eksik varyant ekleme hatası:', error);
    }
  }
  
  // Toplu sepet güncellemesi
  async function batchUpdateCart(updates) {
    if (Object.keys(updates).length === 0) return;
    
    isUpdating = true;
    
    try {
      const formData = new FormData();
      for (const [key, qty] of Object.entries(updates)) {
        formData.append(`updates[${key}]`, qty);
      }
      
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        await refreshCart();
      }
    } catch (error) {
      log('Toplu güncelleme hatası:', error);
    } finally {
      isUpdating = false;
    }
  }
  
  // Sepet sayfası kontrollerini ayarla
  async function setupCartPageControls() {
    if (!window.location.pathname.includes('/cart')) return;

    await groupSetItems();

    // Farklı tema yapılarını destekle — .cart-set-card bu tema'nın özel snippet'i (set ürünler için tek-kart)
    const cartItems = document.querySelectorAll('.cart-set-card, line-item, [data-cart-item], .cart-item, .cart__item, .line-item, tr[data-product-id]');

    log(`Cart items found: ${cartItems.length}`);

    for (const item of cartItems) {
      const handle = getHandleFromItem(item);
      log(`Cart item handle: ${handle}`);

      if (!handle) continue;

      const group = setGroups.get(handle);
      log(`Group for ${handle}:`, group);

      if (!group) continue;

      // Multi-size setler için (Beden Seti ve Multi-size distribution)
      if (group.info.isMultiSize) {
        setupMultiSizeControls(item, handle, group);
        addMultiSizeBadge(item, group);
      }
      // Tek varyantlı setler için (STD, Paket Yapısı, Title-based)
      else if (group.info.multiplier && group.info.multiplier > 1) {
        setupSingleVariantControls(item, handle, group);
        addSingleVariantBadge(item, group.info.multiplier);
      }
      // Multiplier tanımlı ama 1'e eşit olan durumlar için de kontrol ekle
      else if (group.multiplier && group.multiplier > 1) {
        // group.multiplier kullan (groupSetItems'dan gelen)
        setupSingleVariantControls(item, handle, group);
        addSingleVariantBadge(item, group.multiplier);
      }
    }

    // Kaldır linklerini intercept et - YENİ!
    interceptRemoveLinks();
  }
  
  // Multi-size set kontrolleri
  function setupMultiSizeControls(item, handle, group) {
    // Zaten kurulmuşsa skip et (duplicate önleme)
    if (item.dataset.setControlsAttached === 'true') {
      log(`Multi-size controls already attached, skipping setup for ${handle}`);
      return;
    }

    log(`=== Setup Multi-Size Controls for ${handle} ===`);

    const quantityInput = item.querySelector('input[name*="updates"], input.quantity-selector__input, input.quantity__input, input[type="number"]');
    log(`Quantity input found:`, quantityInput !== null);

    if (!quantityInput) {
      log(`⚠️ No quantity input found, skipping`);
      return;
    }

    // Renk filtresi: kart sadece kendi rengine ait line item'ları temsil ediyor (data-set-color attribute).
    // Aynı ürünün GREEN ve BLUE renkleri ayrı kart, ayrı hesap.
    const cardEl = item.classList.contains('cart-set-card') ? item : item.closest('.cart-set-card');
    const cardColor = (cardEl && cardEl.dataset && cardEl.dataset.setColor) || '';
    const filteredItems = cardColor
      ? group.items.filter(li => getItemColorKey(li) === cardColor)
      : group.items;
    log(`Card color: "${cardColor}", filtered items: ${filteredItems.length}/${group.items.length}`);

    // GERÇEK SET SAYISI hesabı — filtered items üzerinde
    let totalActual = 0, totalExpected = 0;
    for (const li of filteredItems) {
      totalActual += li.quantity;
      totalExpected += li.expectedQty || 1;
    }
    if (totalExpected > 0) {
      const setCount = Math.max(1, Math.round(totalActual / totalExpected));
      if (parseInt(quantityInput.value) !== setCount) {
        quantityInput.value = setCount;
        log(`Quantity input set to set count: ${setCount} (was actual qty ${totalActual})`);
      }
    }
    // Not: Eski "compound = line_item.quantity" override'ı kaldırıldı.
    // Artık compound variant'lar için expectedQty = totalPerSet, dolayısıyla setCount yukarıdaki hesapla doğru çıkar.

    // Card için renk-aware operasyon: handler'lara cardColor closure'da iletilir (updateMultiSizeSet 3. param)
    item.dataset.setCardColor = cardColor;

    // Flicker'ı önle: quantity value JS tarafından doğrulandı, kart artık "ready"
    if (cardEl) cardEl.dataset.setReady = 'true';

    // Parent row'u bul (table yapısında desktop button'lar başka td'de olabilir)
    const parentRow = item.closest('tr');
    const searchScope = parentRow || item; // Row varsa row'da ara, yoksa item'da
    log(`Search scope:`, parentRow ? 'table row' : 'line-item');

    // TÜM button'ları bul (mobile + desktop için - row içindeki TÜM button'lar)
    const allButtons = searchScope.querySelectorAll('.quantity-selector__button, [name="plus"], [name="minus"], .quantity__button');
    log(`All buttons found: ${allButtons.length}`);

    // Plus ve minus button'ları ayır
    const plusButtons = [];
    const minusButtons = [];

    allButtons.forEach((btn, index) => {
      const ariaLabel = btn.getAttribute('aria-label') || '';
      const html = btn.innerHTML || '';

      // Plus button mu kontrol et (çift sayılı indexler genelde plus)
      if (index % 2 === 1 || html.includes('icon-plus') || ariaLabel.toLowerCase().includes('artır') || ariaLabel.toLowerCase().includes('increase')) {
        plusButtons.push(btn);
      }
      // Minus button
      else if (index % 2 === 0 || html.includes('icon-minus') || ariaLabel.toLowerCase().includes('azalt') || ariaLabel.toLowerCase().includes('decrease')) {
        minusButtons.push(btn);
      }
    });

    log(`Plus buttons: ${plusButtons.length}, Minus buttons: ${minusButtons.length}`);

    // TÜM Plus button'lara handler ekle
    plusButtons.forEach((plusBtn, idx) => {
      // href'i disable et
      if (plusBtn.getAttribute('href')) {
        plusBtn.dataset.originalHref = plusBtn.getAttribute('href');
        plusBtn.setAttribute('href', 'javascript:void(0)');
        log(`Plus button ${idx} href disabled`);
      }

      plusBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        log(`Plus button ${idx} clicked for multi-size ${handle} (color: ${cardColor})`);
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => updateMultiSizeSet(handle, 'increase', cardColor), 300);
      }, { capture: true });
      log(`✓ Plus button ${idx} handler attached (capture mode)`);
    });

    minusButtons.forEach((minusBtn, idx) => {
      if (minusBtn.getAttribute('href')) {
        minusBtn.dataset.originalHref = minusBtn.getAttribute('href');
        minusBtn.setAttribute('href', 'javascript:void(0)');
        log(`Minus button ${idx} href disabled`);
      }

      minusBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        log(`Minus button ${idx} clicked for multi-size ${handle} (color: ${cardColor})`);
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => updateMultiSizeSet(handle, 'decrease', cardColor), 300);
      }, { capture: true });
      log(`✓ Minus button ${idx} handler attached (capture mode)`);
    });

    quantityInput.addEventListener('change', (e) => {
      const value = parseInt(e.target.value) || 0;
      // Toplam adet bazlı hesap: filtered items'ın ortalama expectedQty'sini al
      const referenceItem = filteredItems[0] || group.items[0];
      const referenceExpected = referenceItem ? (referenceItem.expectedQty || 1) : 1;
      const sets = Math.max(value > 0 ? 1 : 0, Math.round(value / referenceExpected));
      log(`Input change: value=${value}, sets=${sets} (color: ${cardColor})`);
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => updateMultiSizeSet(handle, sets, cardColor), 300);
    });

    log(`✓ Multi-size controls setup completed for ${handle}`);
    // İşlem tamamlandı, flag'i set et
    item.dataset.setControlsAttached = 'true';
  }
  
  // Tek varyantlı set kontrolleri
  function setupSingleVariantControls(item, handle, group) {
    // Zaten kurulmuşsa skip et (duplicate önleme)
    if (item.dataset.setControlsAttached === 'true') {
      log(`Controls already attached, skipping setup for ${handle}`);
      return;
    }

    log(`=== Setup Single Variant Controls for ${handle} ===`);

    const quantityInput = item.querySelector('input[name*="updates"], input.quantity-selector__input, input.quantity__input, input[type="number"]');
    log(`Quantity input found:`, quantityInput !== null);

    // Parent row'u bul (table yapısında desktop button'lar başka td'de olabilir)
    const parentRow = item.closest('tr');
    const searchScope = parentRow || item; // Row varsa row'da ara, yoksa item'da
    log(`Search scope:`, parentRow ? 'table row' : 'line-item');

    // TÜM button'ları bul (mobile + desktop için - row içindeki TÜM button'lar)
    const allButtons = searchScope.querySelectorAll('.quantity-selector__button, [name="plus"], [name="minus"], .quantity__button');
    log(`All buttons found: ${allButtons.length}`);

    // Plus ve minus button'ları ayır
    const plusButtons = [];
    const minusButtons = [];

    allButtons.forEach((btn, index) => {
      const ariaLabel = btn.getAttribute('aria-label') || '';
      const html = btn.innerHTML || '';

      // Plus button mu kontrol et (çift sayılı indexler genelde plus)
      if (index % 2 === 1 || html.includes('icon-plus') || ariaLabel.toLowerCase().includes('artır') || ariaLabel.toLowerCase().includes('increase')) {
        plusButtons.push(btn);
      }
      // Minus button
      else if (index % 2 === 0 || html.includes('icon-minus') || ariaLabel.toLowerCase().includes('azalt') || ariaLabel.toLowerCase().includes('decrease')) {
        minusButtons.push(btn);
      }
    });

    log(`Plus buttons: ${plusButtons.length}, Minus buttons: ${minusButtons.length}`);

    if (!quantityInput) {
      log(`⚠️ No quantity input found, skipping`);
      return;
    }

    // Multiplier'ı güvenli şekilde al - group.info.multiplier veya group.multiplier
    const multiplier = group.info.multiplier || group.multiplier || 1;

    // Eğer multiplier 1 ise, set ürünü değildir, kontrol eklemeye gerek yok
    if (multiplier <= 1) {
      log(`Multiplier is 1, skipping set controls`);
      return;
    }

    log(`✓ Setting up controls with multiplier: ${multiplier}`);

    // UI'da SET SAYISI göster (raw line item quantity değil).
    // Backend'e gönderilen quantity = setCount × multiplier ama input gösterimi setCount.
    const rawQty = parseInt(quantityInput.value) || 0;
    const setCount = Math.max(1, Math.round(rawQty / multiplier));
    if (parseInt(quantityInput.value) !== setCount) {
      quantityInput.value = setCount;
      log(`Quantity input set to setCount: ${setCount} (raw line qty was: ${rawQty})`);
    }
    // Quantity rounding düzeltmesi: line item quantity setCount × multiplier'la uyumlu mu?
    const expectedRawQty = setCount * multiplier;
    if (rawQty !== expectedRawQty && expectedRawQty > 0) {
      const lineKey = quantityInput.dataset.lineKey || quantityInput.getAttribute('data-line-key');
      updateSingleItem(lineKey, expectedRawQty);
      log(`Backend quantity adjusted: ${rawQty} → ${expectedRawQty}`);
    }

    // Flicker önle: ready işareti
    const cardEl = item.classList.contains('cart-set-card') ? item : item.closest('.cart-set-card');
    if (cardEl) cardEl.dataset.setReady = 'true';

    // TÜM Plus button'lara handler ekle (mobile + desktop)
    plusButtons.forEach((plusBtn, idx) => {
      if (plusBtn.getAttribute('href')) {
        plusBtn.dataset.originalHref = plusBtn.getAttribute('href');
        plusBtn.setAttribute('href', 'javascript:void(0)');
        log(`Plus button ${idx} href disabled`);
      }

      plusBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        log(`Plus button clicked for ${handle}`);
        // Input artık setCount gösteriyor — setCount artır, backend'e setCount × multiplier gönder
        const currentSet = parseInt(quantityInput.value) || 0;
        const newSet = currentSet + 1;
        quantityInput.value = newSet;

        const lineKey = quantityInput.dataset.lineKey || quantityInput.getAttribute('data-line-key');
        const newRawQty = newSet * multiplier;
        log(`+1 set: ${currentSet} → ${newSet} (line qty: ${newRawQty})`);
        await updateSingleItem(lineKey, newRawQty);
        showNotification(`+1 set (${multiplier} adet) eklendi`, 'success');
      }, { capture: true });
      log(`✓ Plus button ${idx} handler attached (capture mode)`);
    });

    if (plusButtons.length === 0) {
      log(`⚠️ No plus buttons found`);
    }

    // TÜM Minus button'lara handler ekle (mobile + desktop)
    minusButtons.forEach((minusBtn, idx) => {
      // href'i disable et
      if (minusBtn.getAttribute('href')) {
        minusBtn.dataset.originalHref = minusBtn.getAttribute('href');
        minusBtn.setAttribute('href', 'javascript:void(0)');
        log(`Minus button ${idx} href disabled`);
      }

      minusBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        log(`Minus button clicked for ${handle}`);
        // Input setCount gösteriyor — setCount azalt, backend'e setCount × multiplier gönder
        const currentSet = parseInt(quantityInput.value) || 0;
        const newSet = Math.max(0, currentSet - 1);
        quantityInput.value = newSet;

        const lineKey = quantityInput.dataset.lineKey || quantityInput.getAttribute('data-line-key');
        const newRawQty = newSet * multiplier;
        log(`-1 set: ${currentSet} → ${newSet} (line qty: ${newRawQty})`);
        await updateSingleItem(lineKey, newRawQty);
        if (newSet > 0) {
          showNotification(`-1 set (${multiplier} adet) çıkarıldı`, 'info');
        }
      }, { capture: true });
      log(`✓ Minus button ${idx} handler attached (capture mode)`);
    });

    if (minusButtons.length === 0) {
      log(`⚠️ No minus buttons found`);
    }

    // İşlem tamamlandı, flag'i set et
    item.dataset.setControlsAttached = 'true';
    log(`✓ Controls setup completed for ${handle}`);
  }
  
  // Tek item güncelleme
  async function updateSingleItem(key, quantity) {
    // Eğer key "updates[...]" formatındaysa temizle
    // Değilse direkt lineKey olarak kullan (data-line-key'den gelen format)
    let lineKey = key;
    if (key && key.includes('updates[')) {
      lineKey = key.replace('updates[', '').replace(']', '');
    }

    log(`updateSingleItem: lineKey=${lineKey}, quantity=${quantity}`);
    const updates = { [lineKey]: quantity };
    await batchUpdateCart(updates);
  }
  
  // Multi-size badge
  function addMultiSizeBadge(item, group) {
    // cart-set-card snippet zaten kendi rozetini gösteriyor — duplicate badge ekleme
    if (item.classList && item.classList.contains('cart-set-card')) return;
    if (item.querySelector('.multi-size-badge')) return;

    const badge = document.createElement('div');
    badge.className = 'multi-size-badge';
    
    // XSS koruması: kullanıcı verisini escape et
    const escapeHtml = (str) => str.replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
    const distribution = group.items.map(i => `${escapeHtml(i.size)}-${i.expectedQty}`).join(' ');

    badge.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
        <rect x="3" y="3" width="7" height="7"></rect>
        <rect x="14" y="3" width="7" height="7"></rect>
        <rect x="14" y="14" width="7" height="7"></rect>
        <rect x="3" y="14" width="7" height="7"></rect>
      </svg>
      <span>Set: ${distribution}</span>
    `;
    
    // Single-variant badge ile aynı strateji - farklı tema yapıları için price element bul
    const priceEl = item.querySelector('price-list, .price-list, sale-price, .price, .cart-item__price, .line-item__price');
    if (priceEl) {
      priceEl.parentNode.insertBefore(badge, priceEl);
      log(`✓ Multi-size badge added before price element`);
    } else {
      // Fallback: item'ın başına ekle
      const itemInfo = item.querySelector('.line-item-info, .cart-item__details, .v-stack');
      if (itemInfo) {
        itemInfo.insertBefore(badge, itemInfo.firstChild);
        log(`✓ Multi-size badge added to item info`);
      } else {
        item.appendChild(badge);
        log(`✓ Multi-size badge appended to item`);
      }
    }
  }
  
  // Tek varyant badge
  function addSingleVariantBadge(item, multiplier) {
    // cart-set-card snippet zaten kendi rozetini gösteriyor — duplicate badge ekleme
    if (item.classList && item.classList.contains('cart-set-card')) return;
    if (item.querySelector('.single-variant-badge')) {
      log(`Badge already exists, skipping`);
      return;
    }

    log(`Adding badge for multiplier: ${multiplier}`);

    const badge = document.createElement('div');
    badge.className = 'single-variant-badge';

    badge.innerHTML = `
      <span>📦</span>
      <span>${multiplier}'li Set</span>
    `;

    // Farklı tema yapıları için price element bul
    const priceEl = item.querySelector('price-list, .price-list, sale-price, .price, .cart-item__price, .line-item__price');
    if (priceEl) {
      priceEl.parentNode.insertBefore(badge, priceEl);
      log(`✓ Badge added before price element`);
    } else {
      // Fallback: item'ın başına ekle
      const itemInfo = item.querySelector('.line-item-info, .cart-item__details, .v-stack');
      if (itemInfo) {
        itemInfo.insertBefore(badge, itemInfo.firstChild);
        log(`✓ Badge added to item info`);
      } else {
        item.appendChild(badge);
        log(`✓ Badge appended to item`);
      }
    }
  }
  
  // Handle'ı al
  function getHandleFromItem(item) {
    // Önce snippet'in data attribute'unu kontrol et (cart-set-card)
    if (item.dataset && item.dataset.setHandle) {
      return item.dataset.setHandle;
    }
    const link = item.querySelector('a[href*="/products/"]');
    if (link) {
      const url = link.href || link.getAttribute('href');
      const match = url.match(/\/products\/([^?#]+)/);
      if (match) return match[1];
    }
    return null;
  }
  
  // Sepeti yenile
  async function refreshCart() {
    try {
      // Drawer'ı sadece YENİLE, AÇMA (kullanıcı popup istemiyor).
      const cartDrawer = document.querySelector('cart-drawer, .cart-drawer');
      if (cartDrawer && typeof cartDrawer.refresh === 'function') {
        cartDrawer.refresh();
      }

      if (window.location && window.location.pathname && window.location.pathname.includes('/cart')) {
        setTimeout(() => {
          if (window.location && window.location.reload) {
            window.location.reload();
          }
        }, 500);
      }

      const response = await fetch('/cart.js');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const cart = await response.json();

      if (cart && cart.item_count !== undefined) {
        const itemCount = cart.item_count || 0;
        document.querySelectorAll('.cart-count, [data-cart-count]').forEach(el => {
          if (el && el.textContent !== undefined) {
            el.textContent = itemCount;
          }
        });
      }
    } catch (error) {
      console.error('[Cart Set] Error refreshing cart:', error.message || error);
    }
  }
  
  // Mini sepet interceptor
  function interceptMiniCart() {
    const originalFetch = window.fetch;
    
    window.fetch = async function(url, options = {}) {
      const urlStr = typeof url === 'string' ? url : (url && url.url ? url.url : '');
      if ((urlStr.includes('/cart/change') || urlStr.includes('/cart/update')) && !isUpdating) {
        
        try {
          const body = options.body;
          let lineKey, requestedQty;
          
          if (body instanceof FormData) {
            lineKey = body.get('id');
            requestedQty = parseInt(body.get('quantity')) || 0;
          } else if (typeof body === 'string') {
            const json = JSON.parse(body);
            lineKey = json.id;
            requestedQty = json.quantity;
          }
          
          const cart = await originalFetch('/cart.js').then(r => r.json());
          const cartItem = cart.items.find(i => i.key === lineKey);
          
          if (cartItem) {
            const handle = cartItem.handle;
            await groupSetItems();

            const group = setGroups.get(handle);
            if (group && group.info.isMultiSize) {
              const currentItem = group.items.find(i => i.key === lineKey);
              if (currentItem) {
                // Renk filtresi: bu cartItem'ın rengini bul, sadece o rengin set'ini güncelle
                const itemColor = getItemColorKey(cartItem);

                if (requestedQty > cartItem.quantity) {
                  await updateMultiSizeSet(handle, 'increase', itemColor);
                } else if (requestedQty < cartItem.quantity) {
                  await updateMultiSizeSet(handle, 'decrease', itemColor);
                }

                return new Response(JSON.stringify({ success: true }), { status: 200 });
              }
            }
          }
        } catch (error) {
          log('Mini cart intercept hatası:', error);
        }
      }
      
      return originalFetch.call(this, url, options);
    };
  }
  
  // Bildirim
  function showNotification(message, type = 'info') {
    const existing = document.querySelector('.cart-set-notification');
    if (existing) existing.remove();
    
    const notification = document.createElement('div');
    notification.className = 'cart-set-notification cart-set-notification--' + type;

    const icons = { success: '✓', info: 'ℹ', warning: '⚠' };
    const escapeHtml = (str) => String(str).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));

    notification.innerHTML = `
      <span class="cart-set-notification__icon">${icons[type] || ''}</span>
      <span class="cart-set-notification__message">${escapeHtml(message)}</span>
    `;

    document.body.appendChild(notification);

    setTimeout(() => {
      notification.classList.add('cart-set-notification--leaving');
      setTimeout(() => notification.remove(), 400);
    }, 3500);
  }
  
  // Event listeners
  function addEventListeners() {
    document.addEventListener('cart:refresh', () => {
      setTimeout(setupCartPageControls, 500);
    });
    
    document.addEventListener('cart:updated', () => {
      setTimeout(setupCartPageControls, 500);
    });
  }
  
  // DOM observer
  function observeChanges() {
    const observer = new MutationObserver(() => {
      if (window.location.pathname.includes('/cart')) {
        setupCartPageControls();
      }
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
  
  // Ana başlatma
  async function init() {
    log('Cart Set Multiplier başlatılıyor...');
    // Stiller theme/assets/set-multiplier.css üzerinden yüklenir; inline injection yok.
    interceptMiniCart();
    await setupCartPageControls();
    addEventListeners();
    observeChanges();

    log('Hazır! Set grupları:', setGroups);
  }
  
  // DOM hazır olduğunda başlat
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
})();